// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: LGPL-2.1-only

import type { ComponentInterface, EventEmitter } from "@stencil/core";
import { Component, Event, h, Host, Prop, Watch } from "@stencil/core";

export type Layer = "main" | "panel" | "popup";
export type Level = "section" | "subsection";
export type Tag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

@Component({
  tag: "qds-web-accordion",
  shadow: true,
  styleUrl: "accordion.css",
})
export class Accordion implements ComponentInterface {
  /**
   * The accordion's layer. If defined at the accordion level, it will cascade to all accordion items.
   */
  @Prop() public layer?: Layer = "main";

  /**
   * The accordion's level. If defined at the accordion level, it will cascade to all accordion items.
   */
  @Prop() public level?: Level = "section";

  /**
   * The accordion item title tag. If defined at the accordion level, it will cascade to all accordion items.
   */
  @Prop() public tag?: Tag = "h3";

  /**
   * Wether only one accordion item can be exapanded at a time
   */
  @Prop() public single?: boolean = false;

  /**
   * Emitted when the accordion properties change.
   */
  @Event({
    eventName: "qds-accordion-event",
    cancelable: false,
  })
  public accordionEmitter: EventEmitter<string>;

  @Watch("layer")
  @Watch("level")
  @Watch("tag")
  @Watch("single")
  protected updateAttributes(): void {
    this.accordionEmitter.emit();
  }

  public componentDidLoad(): void {
    this.updateAttributes();
  }

  public render() {
    return (
      <Host layer={this.layer} level={this.level} single={this.single}>
        <div class="accordion">
          <slot />
        </div>
      </Host>
    );
  }
}
